/**
 * Campaign product sections: a scroll-snap carousel that steps one card at a
 * time (works for uneven counts like 4 cards at 3-per-view), plus copy codes.
 */
if (!customElements.get('campaign-products-carousel')) {
  class CampaignProductsCarousel extends HTMLElement {
    constructor() {
      super();
      this.dots = [];
      this.arrows = [];
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onArrowClick = this.onArrowClick.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.campaign-products__grid');
      this.dotsRoot = this.querySelector('.campaign-products__dots');
      this.controls = this.querySelector('.campaign-products__controls');
      this.arrows = Array.from(this.querySelectorAll('.campaign-products__arrow'));
      if (!this.track) return;

      this.arrows.forEach((arrow) => arrow.addEventListener('click', this.onArrowClick));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.rebuildDots();
      this.update();
    }

    disconnectedCallback() {
      this.arrows.forEach((arrow) => arrow.removeEventListener('click', this.onArrowClick));
      this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    get items() {
      return Array.from(this.track.children);
    }

    get maxScroll() {
      return Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    }

    /**
     * Distance between neighbouring card left edges (card width + gap). Using
     * that as the step means 4 cards at 3-per-view correctly get 2 stops, while
     * a full-viewport step would round down to a single page.
     */
    get itemStride() {
      const items = this.items;
      if (items.length < 2) return this.track.clientWidth || 1;
      const stride = items[1].offsetLeft - items[0].offsetLeft;
      return stride > 0 ? stride : this.track.clientWidth || 1;
    }

    get stepCount() {
      const max = this.maxScroll;
      if (max <= 1) return 1;
      return Math.round(max / this.itemStride) + 1;
    }

    /** Offset of the Nth card relative to the first, clamped to scrollable range. */
    offsetForStep(step) {
      const items = this.items;
      if (!items.length) return 0;
      const index = Math.min(Math.max(step, 0), items.length - 1);
      return Math.min(items[index].offsetLeft - items[0].offsetLeft, this.maxScroll);
    }

    rebuildDots() {
      if (!this.dotsRoot) {
        this.dots = [];
        return;
      }

      this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      this.dotsRoot.innerHTML = '';

      const count = this.stepCount;
      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'campaign-products__dot';
        dot.dataset.index = String(i);
        dot.setAttribute('aria-label', 'Slide ' + (i + 1));
        dot.addEventListener('click', this.onDotClick);
        this.dotsRoot.appendChild(dot);
      }

      this.dots = Array.from(this.dotsRoot.children);
    }

    scrollToStep(step) {
      this.track.scrollTo({ left: this.offsetForStep(step), behavior: 'smooth' });
    }

    onArrowClick(event) {
      const direction = event.currentTarget.dataset.direction === 'prev' ? -1 : 1;
      this.scrollToStep(this.activeStep() + direction);
    }

    onDotClick(event) {
      this.scrollToStep(Number(event.currentTarget.dataset.index) || 0);
    }

    onScroll() {
      if (!this.track) return;
      this.update();
    }

    onResize() {
      if (!this.track) return;
      this.rebuildDots();
      this.update();
    }

    activeStep() {
      if (!this.track) return 0;
      const max = this.maxScroll;
      if (max <= 1) return 0;
      if (this.track.scrollLeft >= max - 1) return this.stepCount - 1;
      return Math.min(this.stepCount - 1, Math.round(this.track.scrollLeft / this.itemStride));
    }

    update() {
      if (!this.track) return;
      const max = this.maxScroll;
      const hasOverflow = max > 1;
      const active = this.activeStep();

      if (this.dots.length !== this.stepCount) this.rebuildDots();
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));

      this.arrows.forEach((arrow) => {
        const isPrev = arrow.dataset.direction === 'prev';
        const atEdge = isPrev ? this.track.scrollLeft <= 1 : this.track.scrollLeft >= max - 1;
        const disabled = !hasOverflow || atEdge;
        arrow.disabled = disabled;
        arrow.classList.toggle('is-active', !disabled);
      });

      if (this.controls) this.controls.hidden = !hasOverflow;
    }
  }

  customElements.define('campaign-products-carousel', CampaignProductsCarousel);
}

if (!customElements.get('campaign-products-tabs')) {
  class CampaignProductsTabs extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-tab-target]'));
      this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
      if (!this.buttons.length) return;

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.activate(button.dataset.tabTarget));
      });

      const initial = this.buttons.find((button) => button.classList.contains('is-active')) || this.buttons[0];
      this.activate(initial.dataset.tabTarget);
    }

    activate(id) {
      this.buttons.forEach((button) => {
        const on = button.dataset.tabTarget === id;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      this.panels.forEach((panel) => {
        const on = panel.dataset.tabPanel === id;
        panel.hidden = !on;
        panel.classList.toggle('is-active', on);
      });
    }
  }

  customElements.define('campaign-products-tabs', CampaignProductsTabs);
}

if (!window.campaignCodeCopyBound) {
  window.campaignCodeCopyBound = true;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.campaign-card__code, .campaign-savings-banner__copy');
    if (!button) return;

    const code = button.dataset.code;
    if (!code) return;

    const restore = () => {
      button.textContent = button.dataset.label;
      button.classList.remove('is-copied');
    };

    const confirm = () => {
      button.textContent = button.dataset.copied;
      button.classList.add('is-copied');
      clearTimeout(button.copyTimer);
      button.copyTimer = setTimeout(restore, 2000);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(confirm).catch(() => {
        // Clipboard can reject even on localhost; fall back to execCommand.
        const helper = document.createElement('textarea');
        helper.value = code;
        helper.setAttribute('readonly', '');
        helper.style.position = 'absolute';
        helper.style.left = '-9999px';
        document.body.appendChild(helper);
        helper.select();
        try {
          document.execCommand('copy');
          confirm();
        } catch (err) {
          /* ignore */
        }
        document.body.removeChild(helper);
      });
      return;
    }

    // Fallback for non-HTTPS contexts, where the async clipboard API is unavailable
    const helper = document.createElement('textarea');
    helper.value = code;
    helper.setAttribute('readonly', '');
    helper.style.position = 'absolute';
    helper.style.left = '-9999px';
    document.body.appendChild(helper);
    helper.select();
    document.execCommand('copy');
    document.body.removeChild(helper);
    confirm();
  });
}

if (!window.campaignAddToCartBound) {
  window.campaignAddToCartBound = true;

  const campaignCartSections = () => [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' },
  ];

  const campaignSectionHtml = (html, selector) => {
    if (typeof html !== 'string' || html === '') return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector(selector)?.innerHTML || '';
  };

  const campaignAddUrl = () => {
    const base = window.theme?.routes?.cart_add_url || '/cart/add';
    return base.endsWith('.js') ? base : `${base}.js`;
  };

  const campaignRenderCart = (sections) => {
    let drawerRendered = false;

    campaignCartSections().forEach((section) => {
      const root = document.getElementById(section.id);
      if (!root) return;
      const html = campaignSectionHtml(sections?.[section.section], section.selector);
      if (!html) return;
      (root.querySelector(section.selector) || root).innerHTML = html;
      if (section.id === 'Cart-Drawer') drawerRendered = true;
    });

    return drawerRendered;
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.campaign-card__button[data-variant-id]');
    if (!button || button.disabled || button.classList.contains('loading')) return;

    event.preventDefault();
    const variantId = parseInt(button.dataset.variantId, 10);
    if (!variantId) return;

    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    fetch(campaignAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: campaignCartSections().map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || data.status) {
          console.error('[campaign-products] Add to cart failed', variantId, data);
          return;
        }

        const drawerRendered = campaignRenderCart(data.sections);
        // The add response only returns sections that render on this page; when the
        // drawer markup is missing its contents would stay stale, so pull it fresh.
        if (!drawerRendered) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }

        const drawer = document.getElementById('Cart-Drawer');
        if (drawer) {
          drawer.removeProductEvent?.();
          drawer.open?.();
        }

        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', {
            product: data.items ? data.items[0] : data,
          });
        }
      })
      .catch((error) => {
        console.error('[campaign-products] Add to cart request error', error);
      })
      .finally(() => {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      });
  });
}
